import { A11yModule } from '@angular/cdk/a11y';
import { DialogModule } from '@angular/cdk/dialog';
import { DragDropModule } from "@angular/cdk/drag-drop";
import { LayoutModule } from "@angular/cdk/layout";
import { OverlayModule } from "@angular/cdk/overlay";
import { PortalModule } from '@angular/cdk/portal';
import { CdkScrollableModule, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatMomentDateModule } from "@angular/material-moment-adapter";
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions, MatTooltipModule } from "@angular/material/tooltip";
import { RouterModule } from "@angular/router";
import { NgSelectModule } from '@ng-select/ng-select';
import { PopoverModule } from '@ngx-popovers/popover';
import { NGX_TOOLTIP_CONFIG, NgxTooltip, NgxTooltipConfig } from '@ngx-popovers/tooltip';
import { NgxColorsModule } from 'ngx-colors';
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { GenericAlertDialog } from 'src/app/shared/dialogs/generic-alert/generic-alert.dialog';
import { LoadingModalDialog } from 'src/app/shared/dialogs/loading-modal/loading-modal.dialog';
import { ManageProfileDialog } from 'src/app/shared/dialogs/manage-profile/manage-profile.dialog';
import { SettingsModalDialog } from 'src/app/shared/dialogs/settings-modal/settings-modal.dialog';
import { UnsavedChangesDialog } from 'src/app/shared/dialogs/unsaved-changes/unsaved-changes.dialog';
import { VisualApiExportCopiedSnack } from 'src/app/shared/dialogs/visual-api-export/copied.snack';
import { VisualApiExportDialog } from 'src/app/shared/dialogs/visual-api-export/visual-api-export.dialog';
import { AppMomentFormatsDirective } from 'src/app/shared/directives/app-moment-formats';
import { ClickStopPropagationDirective } from 'src/app/shared/directives/click-stop-propagation.directive';
import { DraggableDirective } from 'src/app/shared/directives/draggable.directive';
import { LetDirective } from 'src/app/shared/directives/let.directive';
import { ResizableDirective } from 'src/app/shared/directives/resizable.directive';
import { ResizeHandleDirective } from 'src/app/shared/directives/resizeHandle.directive';
import { VisualFileExportDialogComponent } from 'src/app/shared/visual-data-export/visual-data-export-dialog.component';
import { BaseComponent } from "../base/base.component";
import { ColorInputComponent } from '../pbir/visuals/base/ColorInput.component';
import { InlineSVGModule } from '../polyfills/ng-inline-svg';
import { AppApiModule } from "../services/api.module";
import { LocalisationService } from "../services/localisation.service";
import { UIModule } from '../ui/ui.module';
import { ContentPlaceholderComponent } from './components/content-placeholder/content-placeholder.component';
import { DashboardPreviewComponent } from './components/dashboard-preview/dashboard-preview.component';
import { SharedDataInputComponent } from './components/data-input/data-input.component';
import { DiagnosticCollectionComponent } from './components/diagnostic-collection/diagnostic-collection.component';
import { DiagnosticDetailsDialogComponent } from './components/diagnostic-collection/diagnostic-details.dialog';
import { InsightsContentListComponent } from './components/insights-content-list/insights-content-list.component';
import { SharedLayoutContentWrapperComponent } from './components/layout/content-wrapper/content-wrapper.component';
import { FooterEtlStatusBannerComponent } from './components/layout/footer/footer-etl-status-banner/footer-etl-status-banner.component';
import { FooterComponent } from './components/layout/footer/footer.component';
import { SharedLayoutSidePanelComponent } from './components/layout/side-panel/side-panel.component';
import { SharedLayoutTopbarComponent } from './components/layout/topbar/topbar.component';
import { LoadingRectComponent } from './components/loading/loading-rect.component';
import { AssetLockIconComponent } from './components/locking/AssetLockIcon.component';
import { RelativeDateRangeComponent } from './components/relative-date-range/relative-date-range.component';
import { InfoSnackComponent } from './components/snacks/info-snack/info-snack.component';
import { AppSnackComponent } from './components/snacks/snack.component';
import { AppSpinnerComponent } from './components/spinner/spinner.component';
import { TextHighlightComponent } from './components/text-highlight/text-highlight.component';
import { NotificationToastComponent } from './components/toast/notification-toast/notification-toast.component';
import { ToastHostComponent } from './components/toast/toast-host/toast-host.component';
import { SharedTrgCardListItemComponent } from './components/trg-card-list/trg-card-list-item.component';
import { SharedTrgCardListComponent } from './components/trg-card-list/trg-card-list.component';
import { SharedTrgCheckboxComponent } from './components/trg-checkbox/trg-checkbox.component';
import { SharedTrgEditorContainerComponent } from './components/trg-editor-container/trg-editor-container.component';
import { SharedTrgSavingOverlayComponent } from './components/trg-saving-overlay/trg-saving-overlay.component';
import { TrgTabsComponent } from './components/trg-tabs/trg-tabs.component';
import { SharedTrgToggleComponent } from './components/trg-toggle/trg-toggle.component';
import { CamelCasePipe } from './pipes/camel-case.pipe';
import { SettingsDrivenCurrencyPipe } from './pipes/currency.pipe';
import { RelativeDateTimePipe } from './pipes/date-relative.pipe';
import { DateWithTimezonePipe } from './pipes/date-with-timezone.pipe';
import { SettingsDrivenDatePipe } from './pipes/date.pipe';
import { SettingsDrivenDecimalPipe } from './pipes/decimal.pipe';
import { EnumToArrayPipe } from './pipes/enumToArray.pipe';
import { FilterPipe } from './pipes/filter.pipe';
import { LocalDatePipe } from './pipes/local-date.pipe';
import { SettingsDrivenPercentPipe } from './pipes/percent.pipe';
import { ToWordsPipe } from './pipes/toWords.pipe';
import { ToWordsOrdinalPipe } from './pipes/toWordsOrdinal.pipe';

const EXPORTS = [
  BaseComponent,

  SharedTrgCheckboxComponent,
  SharedLayoutContentWrapperComponent,
  SharedLayoutSidePanelComponent,
  SharedLayoutTopbarComponent,
  FooterComponent,
  FooterEtlStatusBannerComponent,
  AppSpinnerComponent,
  NotificationToastComponent,
  ToastHostComponent,
  AppSnackComponent,
  InfoSnackComponent,
  SharedDataInputComponent,

  SharedTrgCardListComponent,
  SharedTrgCardListItemComponent,
  SharedTrgSavingOverlayComponent,
  SharedTrgToggleComponent,

  DiagnosticCollectionComponent,
  DiagnosticDetailsDialogComponent,

  TextHighlightComponent,

  SharedTrgEditorContainerComponent,

  InsightsContentListComponent,
  TrgTabsComponent,
  RelativeDateRangeComponent,

  DashboardPreviewComponent,
  ContentPlaceholderComponent,
    
  CamelCasePipe,
  SettingsDrivenDatePipe,
  SettingsDrivenDecimalPipe,
  SettingsDrivenCurrencyPipe,
  SettingsDrivenPercentPipe,
  FilterPipe,
  ToWordsPipe,
  ToWordsOrdinalPipe,
  LocalDatePipe,
  EnumToArrayPipe,
  DateWithTimezonePipe,
  RelativeDateTimePipe,
  UnsavedChangesDialog,
  GenericAlertDialog,
  SettingsModalDialog,
  VisualFileExportDialogComponent,
  ManageProfileDialog,
  VisualApiExportDialog,
  VisualApiExportCopiedSnack,
  LoadingModalDialog,
  ClickStopPropagationDirective,
  LetDirective,
  ResizableDirective,
  ResizeHandleDirective,
  DraggableDirective,
  AppMomentFormatsDirective,
  AssetLockIconComponent,
];

@NgModule({
  imports: [
    InlineSVGModule.forRoot({ bypassHttpClientInterceptorChain: true }),

    A11yModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,

    DragDropModule,
    LayoutModule,
    NgxMatSelectSearchModule,
    OverlayModule,

    AppApiModule,
    
    MatAutocompleteModule,
    MatCardModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatDialogModule,
    MatSelectModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
    MatMenuModule,
    CdkScrollableModule,
    ScrollingModule,

    UIModule,

    NgSelectModule,
    
    NgxColorsModule,
    DialogModule,
    PortalModule,
    ColorInputComponent,
    NgxTooltip,
    PopoverModule,

    NgxMatSelectSearchModule,
    MatProgressSpinnerModule,
    
    LoadingRectComponent,
  ],
  exports: [
    InlineSVGModule,

    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,

    DragDropModule,
    LayoutModule,
    NgxMatSelectSearchModule,
    OverlayModule,

    MatAutocompleteModule,
    MatCardModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatDialogModule,
    MatSelectModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
    MatMenuModule,
    CdkScrollableModule,
    ScrollingModule,

    UIModule,

    NgSelectModule,

    NgxColorsModule,
    DialogModule,
    PortalModule,
    ColorInputComponent,
    NgxTooltip,
    PopoverModule,

    NgxMatSelectSearchModule,
    
    LoadingRectComponent,

    ...EXPORTS,
  ],
  declarations: [
    ...EXPORTS
  ],
  providers: [
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: <MatTooltipDefaultOptions>{
        position: 'below',
        showDelay: 600,
        hideDelay: 400,
        disableTooltipInteractivity: true,
      }
    },
    LocalisationService,
    {
      provide: NGX_TOOLTIP_CONFIG,
      useValue: new NgxTooltipConfig({
        debounce: 50,
        placement: 'bottom-end',
      })
    }
  ]
})
export class SharedModule {
}
